/* eslint-disable consistent-return, no-param-reassign */
import { SetStateAction, useCallback, useEffect, useRef } from "react";

import { IParsedParameter } from "web/types/PageGenerator";

interface UseSliderAccessibilityArgs {
  setCurrentSlide: (value: SetStateAction<number>) => void;
  slides: IParsedParameter[];
}

const useSliderAccessibility = ({
  setCurrentSlide,
  slides,
}: UseSliderAccessibilityArgs) => {
  const carouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = carouselRef.current;
    if (!container) return;

    const FOCUSABLE =
      'a, button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])';

    const root = container.querySelector(".slider-wrapper") as Element;
    const slideElements = container.querySelectorAll(".slide");

    if (!root || !slideElements.length) return;

    const setSlideAccessibility = (slide: Element, visible: boolean): void => {
      const htmlSlide = slide as HTMLElement;

      if (visible) {
        htmlSlide.removeAttribute("aria-hidden");

        htmlSlide.querySelectorAll<HTMLElement>(FOCUSABLE).forEach((el) => {
          if (el.dataset.prevTabindex) {
            el.tabIndex = Number(el.dataset.prevTabindex);
            delete el.dataset.prevTabindex;
          } else {
            el.removeAttribute("tabindex");
          }
        });
      } else {
        htmlSlide.setAttribute("aria-hidden", "true");

        htmlSlide.querySelectorAll<HTMLElement>(FOCUSABLE).forEach((el) => {
          if (!el.dataset.prevTabindex) {
            el.dataset.prevTabindex = String(el.tabIndex ?? 0);
          }
          el.tabIndex = -1;
        });
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setSlideAccessibility(entry.target, entry.isIntersecting);
        });
      },
      { root, threshold: 0.6 }
    );

    slideElements.forEach((slide) => observer.observe(slide));

    return () => {
      observer.disconnect();
    };
  }, [slides]);
  const handleKeyboardNavigation = useCallback((event: KeyboardEvent) => {
    if (event.key === "ArrowLeft") {
      setCurrentSlide((state) => (state ? state - 1 : slides.length - 1));
    } else if (event.key === "ArrowRight") {
      setCurrentSlide((state) => (state === slides.length - 1 ? 0 : state + 1));
    }
  }, []);

  const enableKeyboardNavigation = () => {
    document.addEventListener("keydown", handleKeyboardNavigation);
  };

  const disableKeyboardNavigation = () => {
    document.removeEventListener("keydown", handleKeyboardNavigation);
  };
  return {
    enableKeyboardNavigation,
    disableKeyboardNavigation,
    carouselRef,
  };
};

export default useSliderAccessibility;
